export const LEGAL_URLS = {
  terms: 'https://dreamable.kids/nutzungsbedingungen',
  privacy: 'https://dreamable.kids/datenschutz',
  impressum: 'https://dreamable.kids/impressum',
  faq: 'https://dreamable.kids/faq',
} as const;

/**
 * Feature flag — Web Push delivery wiring (PWA service-worker + backend
 * push subscriptions endpoint). Default `false` at Mai-Launch per
 * Batch 9C Flag 8: the A-14 push-toggle UI is render-gated by this
 * flag combined with `supportsPushNotifications()`. When the flag is
 * false, the toggle section is HIDDEN entirely (no informational text,
 * no "coming soon" disclosure — clean omission).
 *
 * Flip to `true` when the post-launch Web Push integration ships
 * (PWA/Offen #34).
 */
export const WEB_PUSH_DELIVERY_ENABLED: boolean =
  import.meta.env.VITE_WEB_PUSH_DELIVERY_ENABLED === 'true';

/**
 * Feature flag — Series/Auto-Staffel UI (B3). Default `false`: when off,
 * the PWA is byte-for-byte today's behaviour (no Serie toggle, no series
 * routes, no Serien filter/cards, and NO capability probe).
 *
 * This is the BUILD-TIME master switch. When true, the live on/off is
 * deferred to backend capability (see `useSeriesEnabled` → GET /series
 * 200/404), so the eventual production flip is backend-only (flip Railway
 * SERIES_ENABLED) with no coordinated frontend rebuild. The backend ships
 * dormant (SERIES_ENABLED=false → 404), so even a flag-true PWA stays dark
 * until the backend flag is flipped.
 */
export const SERIES_ENABLED: boolean = import.meta.env.VITE_SERIES_ENABLED === 'true';

/**
 * Wissenswelten catalog feature flag (Block C2), same two-layer pattern as
 * SERIES_ENABLED: this build-time master switch must be true AND the backend
 * capability probe (see `useCatalogEnabled` → GET /api/catalog/library 200/404)
 * must succeed. Backend ships dormant (CATALOG_ENABLED=false → 404), so a
 * flag-true PWA stays dark until Railway CATALOG_ENABLED is flipped. When this
 * is false the library makes NO catalog call and is byte-identical to today.
 */
export const CATALOG_ENABLED: boolean = import.meta.env.VITE_CATALOG_ENABLED === 'true';

/**
 * P2 book flow build-time master switch (P5-4 Commit A, 2026-08-04).
 * Default false: when off, the entire book route subtree is absent from
 * App.tsx's Routes array — route registration is gated at the JSX level
 * (`{BOOK_ENABLED && <Route …>}`), which Vite/Rollup fold to dead code at
 * build time so the book feature module and its dependency graph drop out
 * of the production bundle. See P5-4 Order R3.
 *
 * Deliberately different from SERIES_ENABLED / CATALOG_ENABLED, which
 * carry a two-layer pattern (build flag + backend capability probe).
 * The book flow has no backend capability probe today — the routes are
 * simply present or absent per this flag, and any authenticated user can
 * enter the flow when the flag is true. A capability probe will land if
 * / when the backend introduces a book-flow gate.
 */
export const BOOK_ENABLED: boolean = import.meta.env.VITE_BOOK_ENABLED === 'true';

/**
 * Cloudflare Turnstile site key (§A10 signup protection, 2026-08-06).
 * The paired secret lives backend-side as `TURNSTILE_SECRET_KEY`; this
 * site key is browser-embedded (baked into the bundle) and safe to
 * publish.
 *
 * Dev keys documented at
 * https://developers.cloudflare.com/turnstile/troubleshooting/testing/
 *
 *   1x00000000000000000000AA   always PASSES, widget visible (dev happy-path)
 *   2x00000000000000000000AB   always BLOCKS, widget visible (dev rejection-path)
 *   3x00000000000000000000FF   always PASSES, widget invisible (dev headless)
 *   1x00000000000000000000BB   forces interactive challenge (dev UX check)
 *
 * An empty value means Turnstile is not configured — consumers should
 * fail-loud (widget refuses to render + console.error) rather than
 * silently no-op, otherwise the backend's mandatory `turnstileToken`
 * field on /register would 400 with no visible cause on the client.
 */
export const TURNSTILE_SITE_KEY: string = import.meta.env.VITE_TURNSTILE_SITE_KEY ?? '';

export const ASSETS = {
  kaptnKuschelImage: '/worlds/kp/kaptn-kuschel.png',
  kaptnKuschelSittingImage: '/worlds/kp/kaptn-kuschel-sitting.png',
  aaFirefighterIllustration: '/worlds/aa/firefighter.svg',
  sbSchlaubergerFoxIllustration: '/worlds/sb/fox.png',
} as const;
